import { apiClient } from "./client";
import type {
  AiActivity,
  AppNotification,
  AuditEntry,
  CustodyEntry,
  DataHealth,
  GovernedSetting,
  Me,
  OperationsHub,
  Release,
  SyncStatus,
  UpdateGovernedSettingRequest
} from "./types";

// Every endpoint Summit Operations serves today (docs/contracts/API_SURFACE.md). Each is one small function so a page
// never builds a URL by hand and a test can replace exactly one call.

export const summitOpsApi = {
  /** Who is signed in, what policies their roles satisfy, the release and the plant date. */
  getMe: async (): Promise<Me> => (await apiClient.get<Me>("/me")).data,

  /** The Operations Hub command centre (M1). Policy OperationsData. */
  getOperationsHub: async (): Promise<OperationsHub> => (await apiClient.get<OperationsHub>("/operations/hub")).data,

  /** Governed lists and thresholds, each with the business rule it serves. */
  getGoverned: async (): Promise<GovernedSetting[]> => (await apiClient.get<GovernedSetting[]>("/admin/governed")).data,

  /**
   * Changes one governed setting. A stale write (the row changed since `expectedUpdatedAtUtc`) answers 428; a refused
   * value or reason answers 400; a role without the setting answers 403. Policy GovernedOperations.
   */
  updateGoverned: async (key: string, body: UpdateGovernedSettingRequest): Promise<GovernedSetting> =>
    (await apiClient.put<GovernedSetting>(`/admin/governed/${encodeURIComponent(key)}`, body)).data,

  /** The audit log. Policy AuditReaders (Admin, Quality). */
  getAudit: async (take = 100): Promise<AuditEntry[]> => (await apiClient.get<AuditEntry[]>("/admin/audit", { params: { take } })).data,

  /** Chains of custody: who signed what, and with what evidence. */
  getCustody: async (take = 100): Promise<CustodyEntry[]> => (await apiClient.get<CustodyEntry[]>("/custody", { params: { take } })).data,

  /** Every call that went through IAiClient, including the refusals. Policy AdminOnly. */
  getAiActivity: async (): Promise<AiActivity> => (await apiClient.get<AiActivity>("/admin/ai-activity")).data,

  /** Freshness, coverage and peg-quality checks. */
  getDataHealth: async (): Promise<DataHealth> => (await apiClient.get<DataHealth>("/admin/data-health")).data,

  /** The GSS sync panel: the Fabric route, the shared gateway, the window and the recent batches. */
  getSyncStatus: async (): Promise<SyncStatus> => (await apiClient.get<SyncStatus>("/admin/data-sync")).data,

  /** The release catalogue. */
  getReleases: async (): Promise<Release[]> => (await apiClient.get<Release[]>("/releases")).data,

  /** This person's notifications. */
  getNotifications: async (): Promise<AppNotification[]> => (await apiClient.get<AppNotification[]>("/notifications")).data
};

export const QK = {
  me: ["me"] as const,
  operationsHub: ["operations", "hub"] as const,
  governed: ["admin", "governed"] as const,
  audit: ["admin", "audit"] as const,
  custody: ["custody"] as const,
  aiActivity: ["admin", "ai-activity"] as const,
  dataHealth: ["admin", "data-health"] as const,
  dataSync: ["admin", "data-sync"] as const,
  releases: ["releases"] as const,
  notifications: ["notifications"] as const
};
